import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight, Loader2, Shirt, Signpost, Tag, GraduationCap, Percent } from "lucide-react";
import api from "@/lib/api";
import Billboard from "@/components/mall/Billboard";
import StorePicker from "@/components/mall/StorePicker";
import { useMall } from "@/context/MallContext";
import { sellersOf } from "@/lib/sellers";
import { SHOP_NAV } from "@/lib/nav";

const ICONS = { shirts: Shirt, signs: Signpost, classifieds: Tag, classes: GraduationCap, wholesale: Percent };
/** Which shops to ask about, per menu item. Pages that are already cross-shop skip the question. */
const ROOM = { shirts: "shirts", signs: "signs" };
const ASK = { shirts: "Which shop prints your shirts?", signs: "Which shop makes your sign?" };

/** The directory's own signs — the same ones the 13thav Demo store hangs, in plain ink. */
export function DirectoryBillboards() {
    const [boards, setBoards] = useState([]);
    useEffect(() => {
        api.get("/billboards/directory").then(({ data }) => setBoards(data.billboards || [])).catch(() => setBoards([]));
    }, []);
    if (!boards.length) return null;
    return (
        <div data-testid="directory-billboards">
            {boards.map((b) => <Billboard key={b.id} board={b} mono />)}
        </div>
    );
}

/** Five doors, same words as the header. Click one and the directory asks which shop you want. */
export function MenuTiles() {
    const { enter } = useMall();
    const navigate = useNavigate();
    const [ask, setAsk] = useState(null);
    const [stores, setStores] = useState([]);
    const [busy, setBusy] = useState(null);

    const walkIn = (n, card) => {
        setAsk(null);
        if (card) enter(card, "directory");
        navigate(card ? `${n.to}?store=${card.slug}` : n.to);
    };
    const click = async (n) => {
        if (!ROOM[n.key]) return navigate(n.to);
        setBusy(n.key);
        const rows = await sellersOf(ROOM[n.key]);
        setBusy(null);
        if (rows.length <= 1) return walkIn(n, rows[0]);
        setStores(rows);
        setAsk(n);
    };

    return (
        <section className="mx-auto max-w-7xl px-4 py-12 sm:px-6" data-testid="directory-menu">
            <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-zinc-500">Shop by product</p>
            <h2 className="mt-2 font-display text-2xl font-extrabold tracking-tight text-zinc-900 sm:text-3xl">What do you need printed?</h2>
            <p className="mt-2 max-w-2xl text-sm text-zinc-600">Pick a menu item. We will ask which shop you want to order from, and then you will be inside that shop.</p>
            <div className="mt-6 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">
                {SHOP_NAV.map((n) => {
                    const Icon = ICONS[n.key];
                    return (
                        <button key={n.key} onClick={() => click(n)} data-testid={`menu-tile-${n.key}`} disabled={!!busy}
                            className="group flex flex-col justify-between border border-zinc-300 bg-white p-4 text-left transition-colors hover:border-zinc-900 disabled:opacity-70">
                            <span className="flex h-9 w-9 items-center justify-center bg-zinc-900 text-white">
                                {busy === n.key ? <Loader2 size={16} className="animate-spin" /> : <Icon size={16} />}
                            </span>
                            <span className="mt-5 block font-display text-base font-extrabold text-zinc-900">{n.label}</span>
                            <span className="mt-1 block text-xs text-zinc-500">{n.short}</span>
                            <span className="mt-3 inline-flex items-center gap-1 text-xs font-bold text-zinc-900 transition-transform group-hover:translate-x-1">
                                {ROOM[n.key] ? "Pick a shop" : "Open"} <ArrowRight size={12} />
                            </span>
                        </button>
                    );
                })}
            </div>
            <StorePicker open={!!ask} title={ask ? ASK[ask.key] : ""} room={ask ? ROOM[ask.key] : ""} stores={stores}
                onPick={(card) => walkIn(ask, card)} onClose={() => setAsk(null)} />
        </section>
    );
}
